import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import axiosInstance from "../../axiosInstance";
import getDecryptData from "../../helpers/getDecryptData";

const defaultStats = {
  totalAppointments: 0,
  monthCompletedAppointments: 0,
  monthRevenueGeneratedByDoctor: 0,
  totalMonthAppointments: 0,
  month: "",
};

const Dashboard = () => {
  const [isUserdata, setIsUserdata] = useState(null);
  const [stats, setStats] = useState(defaultStats);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const userData = await getDecryptData();
        setIsUserdata(userData || null);

        if (!userData?._id) {
          setStats(defaultStats);
          return;
        }

        if (userData.type === 1 && userData.empType === 2) {
          const response = await axiosInstance.get(
            `/admin/dashboard/hospital/${userData._id}`
          );
          setStats(response?.data?.stats || defaultStats);
          return;
        }

        if (userData.type === 1 && userData.empType === 3) {
          const response = await axiosInstance.get(
            `/admin/dashboard/doctor/${userData._id}`
          );
          setStats(response?.data?.stats || defaultStats);
          return;
        }

        setStats(defaultStats);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setStats(defaultStats);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(Number(value || 0));

  const roleTitle =
    isUserdata?.type === 1 && isUserdata?.empType === 2
      ? "Clinic Dashboard"
      : isUserdata?.type === 1 && isUserdata?.empType === 3
      ? "Doctor Dashboard"
      : "Admin Dashboard";

  const statCards = [
    {
      title: "Total Appointment",
      value: stats.totalAppointments,
      color: "primary",
    },
    {
      title: "Month Completed Appointment",
      value: stats.monthCompletedAppointments,
      color: "success",
    },
    {
      title: "Month Revenue Generated",
      value: formatCurrency(stats.monthRevenueGeneratedByDoctor),
      color: "warning",
    },
    {
      title: "Total Month Appointment",
      value: stats.totalMonthAppointments,
      color: "info",
    },
  ];

  const renderMetricDashboard = () => (
    <>
      <div className="py-4">
        <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
          <div>
            <h2 className="h4 mb-1">{roleTitle}</h2>
            <p className="text-muted mb-0">
              Appointment overview {stats.month ? `for ${stats.month}` : ""}
            </p>
          </div>

          {isUserdata?.empType === 2 && (
            <Link to="/all-order-doctor" className="btn btn-gray-800">
              View All Appointment
            </Link>
          )}

          {isUserdata?.empType === 3 && (
            <Link to="/all-order-doctor" className="btn btn-gray-800">
              View My Appointment
            </Link>
          )}
        </div>
      </div>

      <div className="row g-4">
        {statCards.map((card) => (
          <div className="col-12 col-md-6 col-xl-3" key={card.title}>
            <div className={`card border-0 shadow-sm bg-${card.color} text-white h-100`}>
              <div className="card-body">
                <div className="small text-white-50 text-uppercase mb-2">
                  {card.title}
                </div>
                <div className="h2 mb-0 fw-bold">{loading ? "..." : card.value}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-4 mt-1">
        <div className="col-12 col-xl-8">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h3 className="h5 mb-3">Dashboard Summary</h3>
              <p className="text-muted mb-0">
                This dashboard shows appointment totals, completed appointments,
                this month&apos;s appointment count, and this month&apos;s completed
                revenue for the current {isUserdata?.empType === 2 ? "clinic" : "doctor"}.
              </p>
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <h3 className="h5 mb-3">Quick Links</h3>
              <div className="d-grid gap-2">
                <Link to="/all-order-doctor" className="btn btn-outline-gray-700">
                  Appointment List
                </Link>
                {(isUserdata?.type === 0 || isUserdata?.empType === 2) && (
                  <Link to="/create-appointment" className="btn btn-outline-gray-700">
                    Create Appointment
                  </Link>
                )}
                <Link to="/profile/edit" className="btn btn-outline-gray-700">
                  Edit Profile
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const renderAdminDashboard = () => (
    <>
      <div className="py-4">
        <div className="dropdown">
          <Link
            to="/create-user"
            className="btn btn-gray-800 d-inline-flex align-items-center me-2"
          >
            <svg
              className="icon icon-xs me-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            Add User
          </Link>
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-xl-4 col-md-6">
          <div className="card bg-primary text-white mb-4">
            <div className="card-body">All User</div>
            <div className="card-footer d-flex align-items-center justify-content-between">
              <Link className="small text-white stretched-link" to="/all-user">
                View Details
              </Link>
            </div>
          </div>
        </div>

        <div className="col-xl-4 col-md-6">
          <div className="card bg-warning text-white mb-4">
            <div className="card-body">All Appointment</div>
            <div className="card-footer d-flex align-items-center justify-content-between">
              <Link className="small text-white stretched-link" to="/all-order-doctor">
                View Details
              </Link>
            </div>
          </div>
        </div>

        <div className="col-xl-4 col-md-6">
          <div className="card bg-success text-white mb-4">
            <div className="card-body">All Report</div>
            <div className="card-footer d-flex align-items-center justify-content-between">
              <Link className="small text-white stretched-link" to="/all-report">
                View Details
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const shouldShowRoleDashboard =
    isUserdata?.type === 1 && [2, 3].includes(Number(isUserdata?.empType));

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />
        {shouldShowRoleDashboard ? renderMetricDashboard() : renderAdminDashboard()}
      </main>
    </>
  );
};

export default Dashboard;
